import { getActivePhase } from "@/hooks/useCurrentPhase";
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  AlignmentType,
  Header,
  Footer,
  PageNumber,
  BorderStyle,
  Table,
  TableRow,
  TableCell,
  WidthType,
  ShadingType,
  LevelFormat,
  HeadingLevel,
} from "docx";
import {
  AMP_PLAN_STATUS_LABELS,
  AMP_SPECIAL_RISKS,
  type AmpSectionStatus,
  type AmpPlanStatus,
  type AmpSourceMeta,
} from "@/lib/amp/constants";
import { downloadWordDocument } from "@/lib/documentGenerator/exportHistory";
import { createStampForCurrentUser } from "@/lib/documentGenerator/watermark";
import { buildDocument, COLORS, tableOfContents } from "@/lib/documentGenerator/docTheme";
import { documentFooter } from "@/lib/documentGenerator/documentFooter";


export interface AmpWordSection {
  section_key: string;
  title: string;
  status: AmpSectionStatus;
  body: string | null;
  na_motive: string | null;
  legal_reference: string | null;
  sort_order: number;
  source_meta: AmpSourceMeta | null;
  structured_data?: Record<string, any>;
}

export interface AmpWordPlan {
  name: string;
  status: string;
  version: number;
  revision_note?: string | null;
  revised_at?: string | null;
  handover_at?: string | null;
  handover_to?: string | null;
  handover_note?: string | null;
}

const CONTENT_WIDTH = 9360;

const fmt = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString("sv-SE") : "";

const s = (v: any) => (v === undefined || v === null ? "" : String(v));

/* ---------- byggblock ---------- */

const cellBorder = { style: BorderStyle.SINGLE, size: 1, color: COLORS.tableBorder };
const cellBorders = {
  top: cellBorder,
  bottom: cellBorder,
  left: cellBorder,
  right: cellBorder,
};
const cellPadding = { top: 90, bottom: 90, left: 130, right: 130 };

const cell = (
  text: string,
  width: number,
  opts: { bold?: boolean; shaded?: boolean; align?: (typeof AlignmentType)[keyof typeof AlignmentType] } = {}
) =>
  new TableCell({
    width: { size: width, type: WidthType.DXA },
    borders: cellBorders,
    margins: cellPadding,
    ...(opts.shaded ? { shading: { fill: COLORS.tableHeader, type: ShadingType.CLEAR } } : {}),
    children: String(text ?? "")
      .split("\n")
      .map(
        (line) =>
          new Paragraph({
            alignment: opts.align,
            spacing: { after: 0 },
            children: [new TextRun({ text: line, bold: opts.bold })],
          })
      ),
  });


/** Tabell med rubrikrad + valfria datarader */
const gridTable = (
  columnWidths: number[],
  headers: string[],
  rows: string[][]
) =>
  new Table({
    width: { size: CONTENT_WIDTH, type: WidthType.DXA },
    columnWidths,
    rows: [
      new TableRow({
        children: headers.map((h, i) => cell(h, columnWidths[i], { bold: true, shaded: true })),
      }),
      ...rows.map(
        (r) =>
          new TableRow({
            children: r.map((c, i) => cell(s(c), columnWidths[i])),
          })
      ),
    ],
  });

/** Mallens kontaktblock: Företagsnamn/org.nr + representant, Telefon + E-post */
const contactBlock = (value: any, labels?: { left?: string; right?: string }) => {
  const v = value || {};
  const w = [4680, 4680];
  return new Table({
    width: { size: CONTENT_WIDTH, type: WidthType.DXA },
    columnWidths: w,
    rows: [
      new TableRow({
        children: [
          cell(labels?.left ?? "Företagsnamn, org.nr.", w[0], { bold: true, shaded: true }),
          cell(labels?.right ?? "Representant", w[1], { bold: true, shaded: true }),
        ],
      }),
      new TableRow({
        children: [
          cell([s(v.company), s(v.orgNumber)].filter(Boolean).join(", "), w[0]),
          cell(s(v.representative) || s(v.name), w[1]),
        ],
      }),
      new TableRow({
        children: [
          cell("Telefon", w[0], { bold: true, shaded: true }),
          cell("E-post", w[1], { bold: true, shaded: true }),
        ],
      }),
      new TableRow({
        children: [cell(s(v.phone), w[0]), cell(s(v.email), w[1])],
      }),
    ],
  });
};

/** Mallens enkla kontaktrad: Namn och företag | Telefon och mailadress */
const simpleContact = (value: any) => {
  const v = value || {};
  const w = [4680, 4680];
  const name = [s(v.representative) || s(v.name), s(v.company)].filter(Boolean).join(", ");
  const contact = [s(v.phone), s(v.email)].filter(Boolean).join(", ");
  return new Table({
    width: { size: CONTENT_WIDTH, type: WidthType.DXA },
    columnWidths: w,
    rows: [
      new TableRow({
        children: [
          cell("Namn och företag", w[0], { bold: true, shaded: true }),
          cell("Telefon och mailadress", w[1], { bold: true, shaded: true }),
        ],
      }),
      new TableRow({ children: [cell(name, w[0]), cell(contact, w[1])] }),
    ],
  });
};

const p = (text: string, opts: { bold?: boolean; italics?: boolean; after?: number } = {}) =>
  new Paragraph({
    spacing: { after: opts.after ?? 120 },
    children: [new TextRun({ text, bold: opts.bold, italics: opts.italics, size: 22 })],
  });

const h1 = (text: string) =>
  new Paragraph({
    heading: HeadingLevel.HEADING_1,
    spacing: { before: 360, after: 160 },
    children: [new TextRun({ text, bold: true, size: 30 })],
  });

const h2 = (text: string) =>
  new Paragraph({
    heading: HeadingLevel.HEADING_2,
    spacing: { before: 240, after: 120 },
    children: [new TextRun({ text, bold: true, size: 24 })],
  });

const h3 = (text: string) =>
  new Paragraph({
    spacing: { before: 180, after: 80 },
    children: [new TextRun({ text, bold: true, size: 22 })],
  });

const bullets = (items: string[]) =>
  items.map(
    (t) =>
      new Paragraph({
        numbering: { reference: "amp-bullets", level: 0 },
        spacing: { after: 60 },
        children: [new TextRun({ text: t, size: 22 })],
      })
  );

/** Delar upp fritext i stycken */
const textBlock = (text?: string | null) =>
  s(text)
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => p(line));

const spacer = () => new Paragraph({ text: "", spacing: { after: 120 } });

/* ---------- mallens fasta texter ---------- */

const T = {
  revision:
    "Använd revisionshistoriken för att kunna spåra upprättande, löpande anpassningar och överlämnanden mellan byggarbetsmiljösamordnare. Kompletteringar, ändringar och byte av handläggare ska tydligt framgå.",
  bilagor:
    "Arbetsmiljöplanen gäller hela projektet. Nedan listas bilagor som är relevanta för arbetsmiljöarbetet, med den etapp eller det område bilagan avser.",
  otherActivities:
    "Ange vilka driftställen, verksamheter, projekt som pågår inom eller angränsande till arbetsplatsen.",
  fixedOperation:
    "Om ett fast driftställe är ett gemensamt arbetsställe för flera verksamheter, är den som råder över arbetsstället ansvarig för samordningen av arbetsmiljöfrågor. Det ska framgå tydligt i AMP hur samordningen kommer att gå till samt kontaktuppgifter på den person som har fått sig tilldelat det ansvaret. Bas-U ska samråda med samordningsansvarig för ett gemensamt arbetsställe.",
  sharedWorkplace:
    "Vid behov av samordning inom arbetsområdet, med intilliggande entreprenader och med verksamhet/verksamheter på ett fast driftsställe ska samordningsmöten hållas. De ska ske med regelbundenhet. Om det är flera skilda byggprojekt som pågår samtidigt på angränsande eller samma geografiska yta, ska byggherren tydliggöra ansvaret. Det ska tydligt framgå vem som gör vad och hur det följs upp. Om byggprojektet delar transport- och gångvägar och ytor, ska en överordnad funktion som leder och sammankallar till möten utses.",
  hotWork:
    "Inga Heta Arbeten får påbörjas utan att den som är tillståndsansvarig för brandskydd gjort kontroll och lämnat tillstånd. Tillse att Brandskyddsföreningens säkerhetsregler för Heta Arbeten är uppfyllda.",
  electrician:
    "En av entreprenören utsedd person som har kompetens och befogenheter att vidta erforderliga åtgärder vid arbete i elektriska starkströmsanläggningar och som ska säkerställa personsäkerheten på arbetsplatsen.",
  organisation:
    "Nedan redovisas kontaktuppgifter för byggherre samt för byggarbetsmiljösamordnare. Vid byte av byggarbetsmiljösamordnare ska den tidigare samordnaren stå kvar men tidsperioden avslutas.",
  deputy:
    "Nedanstående gäller i de fall byggherren avtalat med annan part för övertaget ansvar enligt AML 3 kap. 7c §.",
  specialRisks:
    "Arbeten med särskild risk för vilka åtgärder ska beskrivas enligt AFS 2023:3.",
  signature:
    "Genom att signera nedan bekräftar vi att vi har tagit del av arbetsmiljöplanen och dess bilagor för projektet samt de risker som är förknippade med arbetena i projektet. Vi är införstådda med att dessa ska beaktas och följas under hela entreprenadtiden.",
};

/* ---------- avsnittsrenderare ---------- */

type Node = Paragraph | Table;

const renderBilagor = (d: Record<string, any>, body?: string | null): Node[] => {
  const rows: any[] = Array.isArray(d.appendices) ? d.appendices : [];
  const out: Node[] = [h1("1. Bilagor"), p(T.bilagor)];
  out.push(
    gridTable(
      [900, 3400, 2000, 1100, 1960],
      ["Dok", "Bilaga", "Etapp / område", "Ingår", "Anteckning"],
      rows.map((r) => [
        s(r.doc),
        s(r.name),
        s(r.scope) || "Hela projektet",
        r.included ? "Ja" : "",
        s(r.note),
      ])
    )
  );
  out.push(...textBlock(body));
  return out;
};

const renderOrientering = (d: Record<string, any>, body?: string | null): Node[] => {
  const out: Node[] = [h1("2. Orientering om projektet")];

  out.push(
    gridTable([9360], ["Projektnamn"], [[s(d.projectName)]]),
    spacer(),
    gridTable(
      [4680, 2340, 2340],
      ["Adress till arbetsplats", "Postnr", "Ort"],
      [[s(d.address), s(d.postalCode), s(d.city)]]
    )
  );
  if (s(d.gps)) {
    out.push(spacer(), gridTable([9360], ["Postadress / GPS-koordinater"], [[s(d.gps)]]));
  }

  out.push(h2("2.1 Projektbeskrivning"), ...textBlock(d.description));

  out.push(h2("2.2 Andra verksamheter och projekt"), p(T.otherActivities, { italics: true }));
  out.push(...textBlock(d.otherActivities));

  out.push(
    h2("2.3 Arbete på fast driftsställe – samordningsansvarig för ordinarie drift"),
    simpleContact(d.fixedOperationContact),
    spacer(),
    p(T.fixedOperation)
  );

  out.push(
    h2("2.4 Arbete på gemensamt arbetsställe där flera byggprojekt pågår samtidigt – överordnad funktion"),
    simpleContact(d.sharedWorkplaceContact),
    spacer(),
    p(T.sharedWorkplace)
  );

  out.push(
    h2("2.5 Brandskyddsansvarig under byggtiden"),
    simpleContact(d.fireSafetyContact)
  );

  out.push(
    h2("2.6 Tillståndsansvarig för arbetstillstånd"),
    simpleContact(d.permitResponsibleContact)
  );

  const hw = d.hotWorkPermit || {};
  out.push(
    h2("2.7 Tillståndsansvarig för heta arbeten"),
    new Table({
      width: { size: CONTENT_WIDTH, type: WidthType.DXA },
      columnWidths: [4680, 4680],
      rows: [
        new TableRow({
          children: [
            cell("Företag", 4680, { bold: true, shaded: true }),
            cell("Ansvarigt fr.o.m. datum–t.o.m. datum", 4680, { bold: true, shaded: true }),
          ],
        }),
        new TableRow({
          children: [cell(s(hw.company), 4680), cell(s(hw.period) || s(hw.validity), 4680)],
        }),
        new TableRow({
          children: [
            cell("Tillståndsansvarig", 4680, { bold: true, shaded: true }),
            cell("Telefon", 4680, { bold: true, shaded: true }),
          ],
        }),
        new TableRow({
          children: [cell(`(Ansvarig) ${s(hw.responsible)}`.trim(), 4680), cell(s(hw.phone), 4680)],
        }),
        new TableRow({
          children: [cell(`(Delegerad) ${s(hw.delegated)}`.trim(), 4680), cell("", 4680)],
        }),
      ],
    }),
    spacer(),
    p(T.hotWork)
  );

  out.push(
    h2("2.8 Behörig elinstallatör"),
    simpleContact(d.authorizedElectrician),
    spacer(),
    p(T.electrician)
  );

  out.push(...textBlock(body));
  return out;
};

const renderOrganisation = (d: Record<string, any>, body?: string | null): Node[] => {
  const out: Node[] = [h1("3. Arbetsmiljöorganisation"), p(T.organisation)];

  out.push(h2("3.1 Byggherre"), contactBlock(d.client, { right: "Byggherrerepresentant" }));
  out.push(
    h2("3.2 Byggherrens ställföreträdare"),
    p(T.deputy),
    contactBlock(d.clientRepresentative, { right: "Byggherrerepresentant" })
  );
  out.push(
    h2("3.3 Byggarbetsmiljösamordnare för planering och projektering (Bas-P)"),
    contactBlock(d.basP)
  );
  out.push(
    h2("3.4 Byggarbetsmiljösamordnare för utförandet (Bas-U)"),
    contactBlock(d.basU)
  );

  const defaultRoles = [
    "Ombud",
    "Projektchef",
    "Produktionschef/Platschef",
    "Projektledning/Arbetsledare",
  ];
  const siteRows: any[] = Array.isArray(d.siteOrganization) ? d.siteOrganization : [];
  const merged = defaultRoles.map((role) => {
    const match = siteRows.find(
      (r) => s(r.role).toLowerCase().trim() === role.toLowerCase()
    );
    return [role, s(match?.name), s(match?.phone)];
  });
  const extra = siteRows
    .filter((r) => !defaultRoles.some((role) => role.toLowerCase() === s(r.role).toLowerCase().trim()))
    .map((r) => [s(r.role), s(r.name), s(r.phone)]);

  out.push(
    h2("3.5 Arbetsplatsorganisation"),
    gridTable([3600, 3600, 2160], ["Funktion", "Namn", "Telefon"], [...merged, ...extra])
  );

  out.push(...textBlock(body));
  return out;
};

const renderIntroduktion = (d: Record<string, any>, body?: string | null): Node[] => {
  const out: Node[] = [h1("4. Introduktion på byggarbetsplatsen")];
  const parts: [string, string][] = [
    ["Syfte", s(d.purpose)],
    ["Ansvar", s(d.responsibility)],
    ["Tidpunkt", s(d.timing)],
    ["Innehåll i introduktionen", s(d.content)],
    ["Språkanpassning", s(d.languageAdaptation)],
    ["Dokumentation", s(d.documentation)],
    ["Konsekvens vid bristande efterlevnad", s(d.nonCompliance)],
  ];
  parts.forEach(([label, value]) => {
    out.push(h3(label));
    out.push(...textBlock(value));
  });
  out.push(
    p("Introduktionen genomförs i enlighet med 4 kap. 15 § AFS 2023:3.", { italics: true })
  );
  out.push(...textBlock(body));
  return out;
};

const renderMoten = (d: Record<string, any>, body?: string | null): Node[] => {
  const out: Node[] = [h1("5. Rutin för arbetsmiljöfrågor på möten")];
  ([
    ["Byggmöten", s(d.buildingMeetings)],
    ["Samordningsmöten", s(d.coordinationMeetings)],
    ["Säkerhetsmöten", s(d.safetyMeetings)],
  ] as [string, string][]).forEach(([label, value]) => {
    out.push(h3(label));
    out.push(...textBlock(value));
  });
  out.push(...textBlock(body));
  return out;
};

const renderKontroll = (d: Record<string, any>, body?: string | null): Node[] => {
  const out: Node[] = [h1("6. Rutin för kontroll och uppföljning av arbetsmiljöarbetet")];
  ([
    ["Skyddsronder", s(d.safetyRounds)],
    ["Stickprovskontroller", s(d.spotChecks)],
    ["Uppföljning av entreprenörernas arbetsmiljöarbete", s(d.contractorFollowUp)],
    ["Struktur för riskhantering i projektet", s(d.riskStructure)],
  ] as [string, string][]).forEach(([label, value]) => {
    out.push(h3(label));
    out.push(...textBlock(value));
  });
  out.push(...textBlock(body));
  return out;
};

const renderSarskildaRisker = (d: Record<string, any>, body?: string | null): Node[] => {
  const risks: any[] = Array.isArray(d.risks) && d.risks.length ? d.risks : AMP_SPECIAL_RISKS;
  const w = [7200, 1080, 1080];

  const rows: TableRow[] = [
    new TableRow({
      children: [
        cell("Arbeten med särskild risk för vilka åtgärder ska beskrivas", w[0], { bold: true, shaded: true }),
        cell("Ja", w[1], { bold: true, shaded: true, align: AlignmentType.CENTER }),
        cell("Nej", w[2], { bold: true, shaded: true, align: AlignmentType.CENTER }),
      ],
    }),
  ];

  risks.forEach((r, i) => {
    rows.push(
      new TableRow({
        children: [
          cell(`${i + 1}. ${s(r.text)}`, w[0]),
          cell(r.applicable === true ? "x" : "", w[1], { align: AlignmentType.CENTER }),
          cell(r.applicable === false ? "x" : "", w[2], { align: AlignmentType.CENTER }),
        ],
      })
    );
    const notes: string[] = [];
    if (s(r.measures)) notes.push(`Åtgärder: ${s(r.measures)}`);
    if (s(r.executionNote)) notes.push(`Bas-U: ${s(r.executionNote)}`);
    if (r.executionDone) notes.push("Åtgärd genomförd i byggskedet.");
    if (notes.length) {
      rows.push(
        new TableRow({
          children: [
            new TableCell({
              width: { size: CONTENT_WIDTH, type: WidthType.DXA },
              columnSpan: 3,
              borders: cellBorders,
              margins: cellPadding,
              children: notes.map(
                (n) => new Paragraph({ children: [new TextRun({ text: n, size: 20, italics: true })] })
              ),
            }),
          ],
        })
      );
    }
  });

  return [
    h1("7. Särskilda arbetsmiljörisker"),
    p(T.specialRisks),
    new Table({
      width: { size: CONTENT_WIDTH, type: WidthType.DXA },
      columnWidths: w,
      rows,
    }),
    ...textBlock(body),
  ];
};

const renderArbetsberedningar = (d: Record<string, any>, body?: string | null): Node[] => {
  const out: Node[] = [h1("8. Arbetsberedningar")];
  out.push(...textBlock(d.description));
  out.push(h3("Arbetsberedningen ska innehålla en tydlig beskrivning av:"));
  out.push(
    ...bullets([
      "vilka arbetsmoment den avser",
      "när arbetet ska utföras",
      "i vilken ordning momenten ska genomföras",
      "identifierade arbetsmiljörisker",
      "beslutade organisatoriska och tekniska skyddsåtgärder",
      "vem som ansvarar för att åtgärderna genomförs",
      "hur kontroll och uppföljning ska ske.",
    ])
  );
  out.push(
    spacer(),
    p(
      "Arbetsberedningen ska vara skriftlig och lämnas till Bas-U för granskning innan arbetet startar. Arbetet får inte påbörjas förrän arbetsberedningen har granskats och godkänts av Bas-U."
    ),
    p(T.signature, { italics: true })
  );

  const sigs: any[] = Array.isArray(d.signatures) ? d.signatures : [];
  const filled = sigs.map((sg) => [
    [s(sg.name), s(sg.role)].filter(Boolean).join(", "),
    "",
    fmt(sg.date) || s(sg.date),
  ]);
  const emptyRows = Array.from({ length: Math.max(8, 12 - filled.length) }, () => ["", "", ""]);
  out.push(
    gridTable(
      [4160, 3120, 2080],
      ["Namnförtydligande", "Signatur", "Datum"],
      [...filled, ...emptyRows]
    )
  );

  out.push(...textBlock(body));
  return out;
};

const RENDERERS: Record<string, (d: Record<string, any>, body?: string | null) => Node[]> = {
  bilagor: renderBilagor,
  orientering: renderOrientering,
  organisation: renderOrganisation,
  introduktion: renderIntroduktion,
  moten: renderMoten,
  kontroll: renderKontroll,
  sarskilda_risker: renderSarskildaRisker,
  arbetsberedningar: renderArbetsberedningar,
};

/* ---------- huvudfunktion ---------- */

export const generateAmpWordDocument = async (
  plan: AmpWordPlan,
  sections: AmpWordSection[],
  projectName?: string
): Promise<string> => {
  const ordered = [...sections].sort((a, b) => a.sort_order - b.sort_order);
  const byKey = new Map(ordered.map((sec) => [sec.section_key, sec]));

  const orientering = byKey.get("orientering")?.structured_data || {};
  const organisation = byKey.get("organisation")?.structured_data || {};
  const planStatus =
    AMP_PLAN_STATUS_LABELS[plan.status as AmpPlanStatus] || plan.status;
  const displayProjectName = s(orientering.projectName) || s(projectName);
  const issuer =
    s(organisation.basP?.representative) ||
    s(organisation.basP?.name) ||
    s(organisation.basP?.company);

  const stamp = await createStampForCurrentUser({
    projectName: displayProjectName,
    docNumber: "12",
    documentName: `Arbetsmiljöplan${displayProjectName ? ` – ${displayProjectName}` : ""}`,
  });

  const children: Node[] = [];

  // Försättsblad – sidhuvudstabell
  children.push(
    gridTable(
      [2340, 3120, 1900, 2000],
      ["Projektnummer", "Projektnamn", "Version", "Status"],
      [[s(orientering.projectNumber), displayProjectName, `${plan.version}.0`, planStatus]]
    ),
    spacer()
  );

  // Titelruta
  children.push(
    new Table({
      width: { size: CONTENT_WIDTH, type: WidthType.DXA },
      columnWidths: [CONTENT_WIDTH],
      rows: [
        new TableRow({
          children: [
            new TableCell({
              width: { size: CONTENT_WIDTH, type: WidthType.DXA },
              borders: cellBorders,
              margins: { top: 320, bottom: 320, left: 200, right: 200 },
              children: [
                new Paragraph({
                  spacing: { after: 120 },
                  children: [new TextRun({ text: "Arbetsmiljöplan", bold: true, size: 44 })],
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: displayProjectName || "[Projekt]",
                      italics: true,
                      size: 28,
                    }),
                  ],
                }),
              ],
            }),
          ],
        }),
      ],
    }),
    spacer()
  );

  children.push(p(`Utfärdad av: ${issuer}`.trim()));

  children.push(...(tableOfContents() as never[]));

  // Upprättande
  children.push(
    h2("Upprättande"),
    gridTable(
      [2600, 3400, 3360],
      ["", "Namn", "Befattning/funktion"],
      [["Upprättad av", issuer, s(organisation.basP?.company) ? `Bas-P, ${s(organisation.basP?.company)}` : "Bas-P"]]
    )
  );

  // Revisionshistorik
  const currentPhaseName = getActivePhase().name;
  const revRows: string[][] = [];
  revRows.push([
    `${plan.version}.0`,
    fmt(plan.revised_at) || new Date().toLocaleDateString("sv-SE"),
    currentPhaseName,
    s(plan.revision_note) || "Upprättande av arbetsmiljöplan",
    issuer,
  ]);
  if (plan.handover_at) {
    revRows.push([
      "",
      fmt(plan.handover_at),
      currentPhaseName,
      `Överlämnad från Bas-P till Bas-U${plan.handover_note ? ` – ${plan.handover_note}` : ""}`,
      s(plan.handover_to),
    ]);
  }
  while (revRows.length < 4) revRows.push(["", "", "", "", ""]);

  children.push(
    h2("Revisionshistorik"),
    p(T.revision),
    gridTable(
      [1200, 1500, 1800, 3260, 1600],
      ["Version", "Datum", "Skede", "Revidering", "Reviderad av"],
      revRows
    )
  );

  // Avsnitt 1–8
  const order = [
    "bilagor",
    "orientering",
    "organisation",
    "introduktion",
    "moten",
    "kontroll",
    "sarskilda_risker",
    "arbetsberedningar",
  ];
  const keys = [
    ...order.filter((k) => byKey.has(k)),
    ...ordered.map((sec) => sec.section_key).filter((k) => !order.includes(k)),
  ];

  keys.forEach((key) => {
    const section = byKey.get(key);
    if (!section) return;
    const renderer = RENDERERS[key];

    if (section.status === "not_applicable") {
      children.push(h1(section.title));
      children.push(
        new Paragraph({
          spacing: { after: 200 },
          children: [
            new TextRun({ text: "Ej aktuellt. ", bold: true, size: 22 }),
            new TextRun({
              text: section.na_motive || "Ingen motivering angiven.",
              size: 22,
            }),
          ],
        })
      );
      return;
    }

    if (renderer) {
      children.push(...renderer(section.structured_data || {}, section.body));
    } else {
      children.push(h1(section.title), ...textBlock(section.body));
    }
  });
  const footer = await documentFooter({ docNumber: "12", documentType: "amp", includeRevisions: false });

  const doc = buildDocument({
    meta: {
      review: footer.review,
      docNumber: "12",
      documentName: "Arbetsmiljöplan",
      projectName: displayProjectName,
      version: `${plan.version}.0`,
      status: planStatus,
      createdBy: issuer,
      date: plan.revised_at,
    },
    stamp,
    // AMP har eget försättsblad enligt mallen
    skipCover: true,
    children: [...(children as never[]), ...(footer.children as never[])] as never,
  });


  const fileName = `${displayProjectName ? `${displayProjectName} - ` : ""}Arbetsmiljöplan v${plan.version}.docx`;
  await downloadWordDocument(doc, fileName, {
    documentType: "amp",
    docNumber: "12",
    documentId: (plan as { id?: string }).id ?? null,
    documentName: `Arbetsmiljöplan${displayProjectName ? ` – ${displayProjectName}` : ""}`,
    snapshot: { plan, sections },
    stamp,
  });

  return fileName;
};
